// React
import React, { useEffect, useState, useCallback, useMemo } from 'react'

// Next
import Head from 'next/head'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { GetStaticProps } from 'next'

// Components
import { MemeEditor } from '@/components/memes/editor'
import { FileUpload } from '@/components/blocks/file-upload'
import { CustomLoader } from '@/components/blocks/custom-loader'

// Types
import {
  BaseCaption,
  Caption,
  MemeTemplate,
  MemeFormat,
  MemeConstants,
} from '@supermeme-ai/types'

// Utils
import { scaleDownImage } from '@supermeme-ai/utils'
import { useSession, useWatermark } from '@/utils/auth/user-context'
import { toBase64FromFile } from '@/utils/index'
import { getBottomTextY } from '@/utils/meme/text'
import { getFileNameFromUrl } from '@/utils/ui/meme/file'
import { getLandingStaticProps } from '@/utils/ui/landing/get-static-props'

// External
import { isDesktop } from 'react-device-detect'
import { useWindowSize } from 'react-use'
import { fromBase64 } from 'js-base64'
import { compressAccurately } from 'image-conversion'
import axios from 'axios'
import { SearchIcon } from 'lucide-react'
import { useTranslation } from 'next-i18next'

const MemeEditorPage: React.FC = () => {
  const router = useRouter()
  const { t } = useTranslation('routes/meme-maker')
  const { t: tEditor } = useTranslation('components/meme-editor')

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: t('meta.title'),
    description: t('meta.description'),
    applicationCategory: 'Image Editor',
    operatingSystem: 'Web',
    offers: {
      '@type': 'Offer',
      category: 'Content Creation Tools',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Supermeme.ai',
      logo: {
        '@type': 'ImageObject',
        url: 'https://supermeme.ai/static/logo-big.png',
      },
    },
    featureList: [
      'Custom Text Positioning',
      'Font Customization',
      'Image Upload',
      'Real-time Preview',
    ],
  }

  // context
  const [watermark] = useWatermark()
  const session = useSession()

  // page state
  const [captions, setCaptions] = useState<Caption[]>([])
  const [header, setHeader] = useState<BaseCaption>({
    text: '',
    fontSize: MemeConstants.headerFooter.initialFontSize,
    fontColor: MemeConstants.headerFooter.fontColor,
    fontFamily: MemeConstants.headerFooter.fontFamily,
  })
  const [footer, setFooter] = useState<BaseCaption>({
    text: '',
    fontSize: MemeConstants.headerFooter.initialFontSize,
    fontColor: MemeConstants.headerFooter.fontColor,
    fontFamily: MemeConstants.headerFooter.fontFamily,
  })
  const [imageSrc, setImageSrc] = useState<string>('')
  const [memeFormat, setMemeFormat] = useState<MemeFormat>('image')
  const [imageWidth, setImageWidth] = useState<number>(0)
  const [imageHeight, setImageHeight] = useState<number>(0)
  const [templateImageLoaded, setTemplateImageLoaded] = useState<boolean>(false)
  const [templateImageLoading, setTemplateImageLoading] = useState(false)
  const [resultId, setResultId] = useState<number>(0)

  // other
  const { width: screenWidth } = useWindowSize()
  const maxDimension = useMemo(
    () => (screenWidth >= 500 ? 500 : window.innerWidth - 32),
    [screenWidth]
  )

  const populateInitialCaptions = useCallback(
    (width: number, height: number) => {
      const text = t('initialCaptions.text')
      const captions: Caption[] = [
        {
          fontSize: MemeConstants.initialFontSize,
          text,
          x: 0,
          y: MemeConstants.topBottomTextPadding,
          fontFamily: null,
          width,
          height: MemeConstants.initialFontSize + 16,
        },
        {
          fontSize: MemeConstants.initialFontSize,
          text,
          x: 0,
          y: getBottomTextY(
            text,
            { width, height },
            MemeConstants.initialFontSize,
            MemeConstants.topBottomTextPadding
          ),
          fontFamily: null,
          width,
          height: MemeConstants.initialFontSize + 16,
        },
      ]
      setCaptions(captions)
    },
    [t]
  )

  const handleImageLoad = useCallback(
    (src: string, width: number, height: number) => {
      setImageSrc(src)
      setImageWidth(Math.round(width))
      setImageHeight(Math.round(height))
      populateInitialCaptions(width, height)
      setMemeFormat('image')
      setTemplateImageLoaded(true)
      setTemplateImageLoading(false)
    },
    [populateInitialCaptions]
  )

  const loadImage = useCallback(
    (src: string) => {
      const image = new Image()
      image.onload = () => {
        const imageDimensions = scaleDownImage(
          image.width,
          image.height,
          maxDimension
        )
        handleImageLoad(src, imageDimensions.width, imageDimensions.height)
      }
      image.src = src
    },
    [maxDimension, handleImageLoad]
  )

  const handleFileUpload = useCallback(
    async (file: Blob) => {
      if (!file) return

      const compressedFile =
        file.size > 1000000 ? await compressAccurately(file, 600) : file
      const base64String = await toBase64FromFile(compressedFile)
      loadImage(base64String)
    },
    [loadImage]
  )

  useEffect(() => {
    const { template, uploadedTemplate } = router.query
    if (!template || templateImageLoaded) return

    setTemplateImageLoading(true)
    const memeTemplate: MemeTemplate = JSON.parse(
      fromBase64(template as string)
    )

    if (memeTemplate.anonymous === true) {
      loadImage(memeTemplate.image_path)
      return
    }

    const imageFileName = getFileNameFromUrl(memeTemplate.image_path)

    axios
      .get<{ base64String: string; fileType: string }>(
        `/api/image/base64?imageName=${imageFileName}&uploadedTemplate=${uploadedTemplate ?? false}`,
        {
          headers: {
            'Content-Type': 'application/json',
            token: session.access_token,
          },
        }
      )
      .then(({ data }) => {
        const src = `data:image/${data.fileType};base64,${data.base64String}`
        loadImage(src)
      })
      .catch((error) => {
        console.error('Error loading template image:', error)
        setTemplateImageLoading(false)
      })
  }, [router.query, templateImageLoaded, loadImage, session])

  const renderUploadSection = useMemo(
    () => (
      <div className='mx-auto text-center'>
        <div className='flex min-h-[390px] w-full flex-col items-center justify-center space-y-8 rounded-lg p-2'>
          {templateImageLoading ? (
            <CustomLoader />
          ) : (
            <>
              <h2 className='text-xl font-bold lg:text-3xl'>
                {t('upload.title')}
              </h2>
              <div className='space-y-8'>
                <FileUpload
                  disabled={false}
                  large={true}
                  onFileAccepted={handleFileUpload}
                />

                <div className='flex w-full items-center justify-center'>
                  <div className='w-full border-t border-solid border-gray-600' />
                  <p className='mx-2 whitespace-nowrap text-xl font-bold'>
                    {t('upload.or')}
                  </p>
                  <div className='w-full border-t border-solid border-gray-600' />
                </div>

                <div>
                  <Link href='/search' className='primary-button-outline'>
                    <SearchIcon className='h-5 w-5' />
                    {t('upload.searchButton')}
                  </Link>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    ),
    [templateImageLoading, handleFileUpload, t]
  )

  const renderEditor = useMemo(
    () => (
      <MemeEditor
        captions={captions}
        setCaptions={setCaptions}
        watermark={watermark}
        maxDimension={maxDimension}
        isDesktop={isDesktop}
        header={header}
        setHeader={setHeader}
        footer={footer}
        setFooter={setFooter}
        resultId={resultId}
        setResultId={setResultId}
        imageSrc={imageSrc}
        memeFormat={memeFormat}
        imageWidth={imageWidth}
        imageHeight={imageHeight}
        isStarredMeme={false}
        starUnstarCallback={() => {}}
        t={tEditor}
      />
    ),
    [
      captions,
      watermark,
      maxDimension,
      header,
      footer,
      imageSrc,
      memeFormat,
      imageWidth,
      imageHeight,
      tEditor,
      resultId,
    ]
  )

  return (
    <>
      <Head>
        <title>{t('meta.title')}</title>
        <meta name='description' content={t('meta.description')} />
        <meta property='og:url' content='https://supermeme.ai/meme-maker' />
        <link rel='canonical' href='https://supermeme.ai/meme-maker' />
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      </Head>
      <div className='mx-auto max-w-full'>
        {!imageSrc ? renderUploadSection : renderEditor}
      </div>
    </>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  return getLandingStaticProps({
    locale,
    namespaces: [
      'common',
      'routes/meme-maker',
      'components/login-form',
      'components/meme-editor',
    ],
  })
}

export default MemeEditorPage
